import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Modal } from '../ui/Modal';
import { UserSelect, useMembers } from '../ui/UserSelect';
import { assignConversation, handoffConversation, listQueues } from './api';

/**
 * Passing a conversation on, from the bar above it (Phase 148).
 *
 * The flow existed, in the panel beside the thread: take it, assign it, move it to a queue. But
 * handing a conversation to somebody else is one of the two or three things an agent does most, and
 * it was behind opening a panel and scrolling — so this is the same operations, one keystroke away,
 * where the rest of the thread's actions already are.
 *
 * Person *or* queue, never both at once. They are different decisions: naming a person says who
 * answers, and choosing a queue says which team does and lets that queue's own strategy pick. Doing
 * both would mean assigning somebody in a queue they are not in, which is how a thread ends up
 * owned by a person nobody on that team can see.
 */

export function TransferDialog({
  open,
  conversationId,
  assigneeId,
  queueId,
  onClose,
}: {
  open: boolean;
  conversationId: string;
  assigneeId: string | null;
  queueId: string | null;
  onClose: () => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const { options: people } = useMembers();

  const [target, setTarget] = useState<'person' | 'queue'>('person');
  const [personId, setPersonId] = useState<string | null>(null);
  const [queue, setQueue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Loaded only once the dialog is actually open: the inbox renders this for every thread.
  const queues = useQuery({ queryKey: ['queues'], queryFn: listQueues, enabled: open });

  // A dialog that reopens holding the last answer is a dialog that transfers to the wrong person.
  useEffect(() => {
    if (!open) return;

    setTarget('person');
    setPersonId(null);
    setQueue('');
    setError(null);
  }, [open]);

  const done = (): void => {
    void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
    void client.invalidateQueries({ queryKey: ['inbox'] });
    onClose();
  };

  const toPerson = useMutation({
    mutationFn: (to: string) => assignConversation(conversationId, to),
    onSuccess: done,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toQueue = useMutation({
    // No assignee: the queue's strategy chooses, which is the reason to move it to a queue at all.
    mutationFn: (to: string) => handoffConversation(conversationId, { queueId: to }),
    onSuccess: done,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const others = (queues.data?.queues ?? []).filter((entry) => entry.id !== queueId);
  const pending = toPerson.isPending || toQueue.isPending;
  const ready = target === 'person' ? personId !== null && personId !== assigneeId : queue !== '';

  const submit = (): void => {
    if (!ready) return;

    if (target === 'person') toPerson.mutate(personId!);
    else toQueue.mutate(queue);
  };

  return (
    <Modal
      open={open}
      title={t('inbox.transfer.title')}
      description={t('inbox.transfer.body')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('confirm.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={!ready || pending}
            onClick={submit}
          >
            {pending ? t('inbox.transfer.sending') : t('inbox.transfer.confirm')}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="segmented" role="tablist" aria-label={t('inbox.transfer.title')}>
          <button
            type="button"
            role="tab"
            className="segmented__option"
            aria-selected={target === 'person'}
            onClick={() => setTarget('person')}
          >
            {t('inbox.transfer.toPerson')}
          </button>
          <button
            type="button"
            role="tab"
            className="segmented__option"
            aria-selected={target === 'queue'}
            onClick={() => setTarget('queue')}
          >
            {t('inbox.transfer.toQueue')}
          </button>
        </div>

        {target === 'person' ? (
          <label className="field">
            <span className="contact-field__label">{t('inbox.transfer.person')}</span>
            <UserSelect
              value={personId}
              // Handing a thread to whoever already holds it is not a transfer.
              options={people.filter((person) => person.id !== assigneeId)}
              onChange={setPersonId}
              placeholder={t('inbox.transfer.pickPerson')}
              emptyLabel={t('inbox.transfer.pickPerson')}
            />
          </label>
        ) : (
          <label className="field">
            <span className="contact-field__label">{t('inbox.transfer.queue')}</span>
            <select
              className="select"
              value={queue}
              onChange={(event) => setQueue(event.target.value)}
            >
              <option value="">{t('inbox.transfer.pickQueue')}</option>
              {others.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>

            {!queues.isLoading && others.length === 0 && (
              <span className="muted small">{t('inbox.noOtherQueue')}</span>
            )}
          </label>
        )}

        {error && (
          <p className="alert alert--compact" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
